import { mat4, vec3 } from 'gl-matrix';

import { Camera } from '../../camera';
import { Context } from '../../context/Context';
import { DirectionalLight } from '../../light';
import { VertexBuffer } from '../../mesh';
import { RenderSettings } from '../../renderer';
import { Scene } from '../../scene/Scene';
import { Sky } from './Sky';
import { createSkyCubeGeometry } from './skyCubeGeometry';

/**
 * Procedurally rendered sky based on analytical Rayleigh + Mie atmospheric
 * scattering. Reads the sun direction from the first {@link DirectionalLight}
 * in the scene's light manager so it stays in lockstep with whatever drives
 * the lighting (e.g. {@link DayNightCycleFeature}).
 */
export class ProceduralSky extends Sky {
  /** Multiplicative tint applied to the sun disc colour. */
  public sunColor: [number, number, number] = [1.0, 0.95, 0.85];

  /** Overall scattering intensity multiplier. ~12 matches a sunny day. */
  public sunIntensity: number = 12.0;

  /** Colour the dome fades toward at the horizon (matches engine fog). */
  public fogColor: [number, number, number] = [0.7, 0.78, 0.85];

  /** 0 = no horizon mist, 1 = sky completely fades to fog at the horizon. */
  public horizonMist: number = 0.25;

  /** Exposure multiplier for the in-shader Reinhard tonemap. */
  public exposure: number = 1.5;

  /** Render the sun disc + halo. Disable on scenes that don't see the sky. */
  public showSun: boolean = true;

  /** Render procedural stars. They auto-fade out during the day. */
  public showStars: boolean = true;

  /** Render the moon disc on the antisolar direction. */
  public showMoon: boolean = true;

  /** Render the 2D FBM dome cloud layer. */
  public showClouds: boolean = true;

  /** Lit (top / sunward) side colour of the clouds. */
  public cloudColor: [number, number, number] = [1.0, 1.0, 1.0];

  /** Shadow (under / shaded) side colour of the clouds. */
  public cloudShadowColor: [number, number, number] = [0.55, 0.6, 0.7];

  /** 0 = clear sky, 1 = fully overcast. */
  public cloudCoverage: number = 0.55;

  /** Opacity multiplier for the cloud layer (0..1). */
  public cloudDensity: number = 0.85;

  /** Cloud feature size — smaller value = larger clouds. */
  public cloudScale: number = 0.4;

  /** Drift velocity in cloud-uv space, applied per second. */
  public cloudWind: [number, number] = [0.012, 0.005];

  /**
   * Direction TOWARD the sun in world space, used as a fallback when no
   * directional light is present in the scene.
   */
  public sunDirection: [number, number, number] = [0.3, 0.7, 0.5];

  /**
   * When set, overrides both the directional-light lookup and the {@link sunDirection}
   * fallback. Useful for preview controllers (e.g. day-night cycle scrubbing) that
   * drive sun position independently of scene lights.
   */
  public sunDirectionOverride: [number, number, number] | null = null;

  private buffer: VertexBuffer;
  private vao: WebGLVertexArrayObject;
  private viewScratch: mat4 = mat4.create();
  private sunScratch: vec3 = vec3.create();

  constructor(
    ctx: Context,
    private scene: Scene,
  ) {
    super();
    const geom = createSkyCubeGeometry(ctx);
    this.buffer = geom.buffer;
    this.vao = geom.vao;
  }

  public render(ctx: Context, camera: Camera, _settings: RenderSettings) {
    if (this.buffer.itemCount === 0) return;

    const gl = ctx.gl;
    const shader = ctx.engine.shaderManager.getProceduralSkyShader();
    ctx.setShader(shader);

    gl.bindVertexArray(this.vao);

    // Take the camera's view matrix as-is. The shader strips translation
    // via mat4(mat3(uView)) so the cube stays centred on the camera.
    mat4.copy(this.viewScratch, camera.modelViewMatrix);

    // Sun direction is "toward the sun" in world space; DirectionalLight
    // stores "direction the light points" — invert.
    if (this.sunDirectionOverride) {
      this.sunScratch[0] = this.sunDirectionOverride[0];
      this.sunScratch[1] = this.sunDirectionOverride[1];
      this.sunScratch[2] = this.sunDirectionOverride[2];
    } else {
      const sun = this.findSun();
      if (sun) {
        this.sunScratch[0] = -sun.direction[0];
        this.sunScratch[1] = -sun.direction[1];
        this.sunScratch[2] = -sun.direction[2];
      } else {
        this.sunScratch[0] = this.sunDirection[0];
        this.sunScratch[1] = this.sunDirection[1];
        this.sunScratch[2] = this.sunDirection[2];
      }
    }
    vec3.normalize(this.sunScratch, this.sunScratch);

    const time = performance.now() / 1000;

    shader.uniformsContainer
      .setProjection(camera.projectionMatrix)
      .setView(this.viewScratch)
      .setSunDirection(this.sunScratch)
      .setSunColor(this.sunColor)
      .setSunIntensity(this.sunIntensity)
      .setTintColor(this.tintColor)
      .setFogColor(this.fogColor)
      .setHorizonMist(this.horizonMist)
      .setExposure(this.exposure)
      .setTime(time)
      .setCloudColor(this.cloudColor)
      .setCloudShadowColor(this.cloudShadowColor)
      .setCloudCoverage(this.cloudCoverage)
      .setCloudDensity(this.cloudDensity)
      .setCloudScale(this.cloudScale)
      .setCloudWind(this.cloudWind)
      .setShowSun(this.showSun ? 1.0 : 0.0)
      .setShowStars(this.showStars ? 1.0 : 0.0)
      .setShowMoon(this.showMoon ? 1.0 : 0.0)
      .setShowClouds(this.showClouds ? 1.0 : 0.0);

    gl.depthMask(false);
    gl.drawArrays(this.buffer.glDrawMode, 0, this.buffer.itemCount);
    gl.depthMask(true);
  }

  private findSun(): DirectionalLight | undefined {
    for (const light of this.scene.lightManager.lights) {
      if (light instanceof DirectionalLight && light.enabled) {
        return light;
      }
    }
    return undefined;
  }

}
